import IModelData from './ModelData';

/**
 * Sections displayed in the Financial Model Editor grid.
 * Order of declaration = order in the UI.
 * Mirror of api/model_scenarios/sections.py (SECTION_INPUT_TYPES keys).
 */
export enum ModelSectionType {
  COMPANY = 'company',
  ASSET = 'asset',
  EQUITY = 'equity',
  CORPORATE_TAXATION = 'corporate_taxation',
  BANK_ACCOUNTS = 'bank_accounts',
  FINANCIAL_DEBT = 'financial_debt',
  INDEXATION = 'indexation_references',
  MACRO_ECONOMICS = 'macro_economics',
  TAXATION = 'fiscality',
}

export interface ModelSectionConfig {
  id: ModelSectionType;
  name: string;
  icon: string;
  description: string;
  color?: string;
}

/** Instance of a section with its populated items. */
export interface ModelSection {
  config: ModelSectionConfig;
  inputItems: IModelData[];
  outputItems: IModelData[];
  itemCount: number;
  hasData: boolean;
}

export const DEFAULT_SECTION_CONFIGS: Record<ModelSectionType, ModelSectionConfig> = {
  [ModelSectionType.COMPANY]: {
    id: ModelSectionType.COMPANY,
    name: 'Company',
    icon: 'apartment',
    description: 'Corporate structure and companies.',
  },
  [ModelSectionType.ASSET]: {
    id: ModelSectionType.ASSET,
    name: 'Asset',
    icon: 'factory',
    description: 'Assets with their CAPEX, revenues, OPEX, quantities and local taxes.',
  },
  [ModelSectionType.EQUITY]: {
    id: ModelSectionType.EQUITY,
    name: 'Equity',
    icon: 'account_balance_wallet',
    description: 'Equity funding, distribution policies and valuation.',
  },
  [ModelSectionType.CORPORATE_TAXATION]: {
    id: ModelSectionType.CORPORATE_TAXATION,
    name: 'Corporate Taxation',
    icon: 'receipt_long',
    description: 'Company-level fiscality: corporate income tax, VAT and property tax.',
  },
  [ModelSectionType.BANK_ACCOUNTS]: {
    id: ModelSectionType.BANK_ACCOUNTS,
    name: 'Bank Accounts',
    icon: 'account_balance',
    description: 'Company bank accounts.',
  },
  [ModelSectionType.FINANCIAL_DEBT]: {
    id: ModelSectionType.FINANCIAL_DEBT,
    name: 'Financial Debt',
    icon: 'request_quote',
    description: 'Long-term debts.',
  },
  [ModelSectionType.INDEXATION]: {
    id: ModelSectionType.INDEXATION,
    name: 'Indexation',
    icon: 'public',
    description: 'Inflation factors.',
  },
  [ModelSectionType.MACRO_ECONOMICS]: {
    id: ModelSectionType.MACRO_ECONOMICS,
    name: 'Macro Economics',
    icon: 'public',
    description: 'Market revenues and other factors.',
  },
  [ModelSectionType.TAXATION]: {
    id: ModelSectionType.TAXATION,
    name: 'Taxation',
    icon: 'balance',
    description: 'Country-level rules governing the collection of taxes.',
  },
};

export function getSectionConfig(type: ModelSectionType): ModelSectionConfig {
  return DEFAULT_SECTION_CONFIGS[type];
}

export function createEmptySection(type: ModelSectionType): ModelSection {
  return {
    config: getSectionConfig(type),
    inputItems: [],
    outputItems: [],
    itemCount: 0,
    hasData: false,
  };
}
